// Project Tracker import (internal) — pick target client → upload the tracker
// spreadsheet → auto-map columns → validate (dry-run) → commit. Creates/updates
// projects (with end-client, subcontractors, works), sites, buildings, contacts
// and live site-team portal accounts.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, ArrowLeft, Wand2, PlayCircle, Plus, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function TrackerImportPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const fileRef = useRef(null);
    const [fields, setFields] = useState([]);
    const [labels, setLabels] = useState({});
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [newClientName, setNewClientName] = useState("");
    const [creatingClient, setCreatingClient] = useState(false);
    const [parsed, setParsed] = useState(null);
    const [mapping, setMapping] = useState({});
    const [createUsers, setCreateUsers] = useState(true);
    const [publishPortal, setPublishPortal] = useState(true);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [committed, setCommitted] = useState(false);
    const [fileName, setFileName] = useState("");

    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const isAdmin = user && (user.role === "director" || user.role === "administrator");

    useEffect(() => {
        if (!isInternal) return;
        api.get("/imports/tracker/fields").then((r) => { setFields(r.data.fields); setLabels(r.data.labels); }).catch(() => {});
        api.get("/imports/tracker/clients").then((r) => setClients(r.data)).catch(() => {});
    }, [isInternal]);

    if (user && !isInternal) {
        return (
            <div className="max-w-[640px] mx-auto py-16 text-center" data-testid="tracker-import-denied">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3" style={{ color: "#C68A1C" }} />
                <h1 className="text-xl font-bold text-[#1F1F1F]">Internal access only</h1>
                <p className="text-[13px] mt-1.5" style={{ color: "#3e4b63" }}>The project tracker import is available to Priority Risk staff only.</p>
            </div>
        );
    }

    async function createClient() {
        if (!newClientName.trim()) return;
        setCreatingClient(true);
        try {
            const { data } = await api.post("/imports/tracker/clients", { name: newClientName.trim() });
            setClients((c) => [...c, data].sort((a, b) => a.name.localeCompare(b.name)));
            setClientId(data.id); setNewClientName("");
            toast.success(`Client created: ${data.name}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create client"); }
        finally { setCreatingClient(false); }
    }

    async function onFile(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name); setResult(null); setCommitted(false);
        const fd = new FormData(); fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/imports/tracker/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setParsed(data); setMapping(data.suggested_mapping || {});
            toast.success(`Parsed ${data.row_count} row(s)`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not parse file"); setParsed(null);
        } finally { setBusy(false); }
    }

    async function run(dryRun) {
        if (!clientId) { toast.error("Choose the target Priority Risk client first"); return; }
        if (!mapping.project_name) { toast.error("Map the Project name column first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/imports/tracker/commit", { client_id: clientId, mapping, rows: parsed.rows, create_users: createUsers, publish_to_portal: publishPortal, dry_run: dryRun });
            setResult(data);
            if (!dryRun) { setCommitted(true); toast.success("Import complete"); }
            else if (data.ok) toast.success("Validation passed — ready to import");
            else toast.error(`${data.errors.length} error(s) — fix and re-validate`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
        } finally { setBusy(false); }
    }

    const c = result?.counters;
    const copyCreds = () => {
        const text = (result?.credentials || []).map((x) => `${x.name}\t${x.email}\t${x.temp_password}\t${x.project}`).join("\n");
        navigator.clipboard?.writeText(`Name\tEmail\tTemp password\tProject\n${text}`);
        toast.success("Credentials copied to clipboard");
    };

    return (
        <div className="max-w-[1000px] mx-auto space-y-5" data-testid="tracker-import-page">
            <button onClick={() => navigate("/app/projects")} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: "#3e4b63" }}>
                <ArrowLeft className="w-4 h-4" /> Back to projects
            </button>
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">Import project tracker</h1>
                <p className="text-[13px] mt-1" style={{ color: "#3e4b63" }}>Upload a construction company's project tracker. All projects import under one Priority Risk client; the spreadsheet's "Client" column is stored as each project's <strong>End client (employer)</strong>. Sub-contractors, works and site-team members are read from the continuation rows beneath each project.</p>
            </div>

            {/* Target client */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }} data-testid="tracker-client-card">
                <h2 className="text-[14px] font-bold text-[#1F1F1F] mb-3">1 · Target Priority Risk client</h2>
                <div className="flex flex-wrap items-end gap-3">
                    <label className="block flex-1 min-w-[220px]">
                        <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>Import all projects under</span>
                        <select data-testid="tracker-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)}
                            className="w-full h-9 px-2.5 rounded-lg border bg-white text-sm" style={{ borderColor: clientId ? "#CBE3C6" : "#E5E0DA" }}>
                            <option value="">— select a client —</option>
                            {clients.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                        </select>
                    </label>
                    {isAdmin && (
                        <div className="flex items-end gap-2">
                            <label className="block">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>…or create a new client</span>
                                <input data-testid="tracker-new-client-name" value={newClientName} onChange={(e) => setNewClientName(e.target.value)}
                                    placeholder="e.g. Saltash Construction" className="h-9 px-2.5 rounded-lg border bg-white text-sm" style={{ borderColor: "#E5E0DA" }} />
                            </label>
                            <button data-testid="tracker-create-client" onClick={createClient} disabled={creatingClient || !newClientName.trim()}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60" style={{ background: "#1f2a44" }}>
                                {creatingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Upload */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }}>
                <h2 className="text-[14px] font-bold text-[#1F1F1F] mb-3">2 · Upload the tracker</h2>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="tracker-file-input" />
                <button onClick={() => fileRef.current?.click()} disabled={busy}
                    className="w-full border-2 border-dashed rounded-[12px] py-10 grid place-items-center gap-2 hover:bg-[#FBF8F3] transition-colors" style={{ borderColor: "#E0D8CC" }}>
                    {busy && !parsed ? <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--brand-accent,#C75A1B)" }} /> : <UploadCloud className="w-7 h-7" style={{ color: "var(--brand-accent,#C75A1B)" }} />}
                    <span className="text-[13px] font-semibold text-[#1F1F1F]">{fileName || "Choose a CSV or XLSX file"}</span>
                    <span className="text-[11.5px]" style={{ color: "#64718a" }}>{parsed ? `${parsed.row_count} rows · ${parsed.columns.length} columns` : "The title banner row is skipped automatically"}</span>
                </button>
            </div>

            {/* Mapping */}
            {parsed && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }} data-testid="tracker-mapping">
                    <h2 className="text-[14px] font-bold text-[#1F1F1F] flex items-center gap-2 mb-3"><Wand2 className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} /> 3 · Map columns</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {fields.map((f) => (
                            <label key={f} className="block">
                                <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#3e4b63" }}>{labels[f] || f}</span>
                                <select data-testid={`tracker-map-${f}`} value={mapping[f] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || undefined }))}
                                    className="w-full h-9 px-2.5 rounded-lg border bg-white text-sm" style={{ borderColor: mapping[f] ? "#CBE3C6" : "#E5E0DA" }}>
                                    <option value="">— not mapped —</option>
                                    {parsed.columns.map((col) => <option key={col} value={col}>{col}</option>)}
                                </select>
                            </label>
                        ))}
                    </div>
                    <label className="flex items-center gap-2 mt-4 text-[12.5px] font-semibold cursor-pointer" style={{ color: "#1f2a44" }} data-testid="tracker-create-users-toggle">
                        <input type="checkbox" checked={createUsers} onChange={(e) => setCreateUsers(e.target.checked)} />
                        Create portal accounts for site-team members (site_manager role, project-scoped)
                    </label>
                    <label className="flex items-center gap-2 mt-2 text-[12.5px] font-semibold cursor-pointer" style={{ color: "#1f2a44" }} data-testid="tracker-publish-toggle">
                        <input type="checkbox" checked={publishPortal} onChange={(e) => setPublishPortal(e.target.checked)} />
                        Publish projects to the portal (site team sees their allocated projects immediately)
                    </label>
                    <div className="flex items-center gap-2 mt-4">
                        <button data-testid="tracker-validate-button" onClick={() => run(true)} disabled={busy}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0] disabled:opacity-60" style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />} Validate (dry-run)
                        </button>
                        <button data-testid="tracker-commit-button" onClick={() => run(false)} disabled={busy || !result || !result.ok || committed}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "#1f2a44" }}>
                            <PlayCircle className="w-4 h-4" /> Import {result?.total_rows ? `${result.total_rows} project(s)` : ""}
                        </button>
                        {result && !result.ok && <span className="text-[11.5px] font-semibold" style={{ color: "#B33A2E" }}>Resolve errors before importing.</span>}
                    </div>
                </div>
            )}

            {/* Result */}
            {result && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-6" style={{ borderColor: "#E5E0DA" }} data-testid="tracker-result">
                    <div className="flex items-center gap-2 mb-3">
                        {committed ? <CheckCircle2 className="w-5 h-5" style={{ color: "#3F7D3A" }} /> : <FileSpreadsheet className="w-5 h-5" style={{ color: "var(--brand-accent,#C75A1B)" }} />}
                        <h2 className="text-[14px] font-bold text-[#1F1F1F]">{committed ? "Import complete" : "Validation preview"}</h2>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
                        {[["projects", "Projects"], ["sites", "Sites"], ["buildings", "Buildings"], ["subcontractors", "Sub-contractors"], ["works", "Works"], ["contacts", "Site team"], ["users", "Portal accounts"]].map(([k, l]) => (
                            <div key={k} data-testid={`tracker-count-${k}`} className="rounded-[10px] border px-3 py-2.5" style={{ borderColor: "#EFEAE3", background: "#FCFBF9" }}>
                                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#64718a" }}>{l}</div>
                                <div className="text-[13px] font-bold text-[#1F1F1F] mt-1">
                                    {c[k].created ?? 0}{c[k].updated != null ? ` · +${c[k].updated} upd` : ""}
                                </div>
                            </div>
                        ))}
                    </div>

                    {result.credentials?.length > 0 && (
                        <div className="rounded-[10px] border p-3 mb-3" style={{ borderColor: "#CBE3C6", background: "#F3FAF2" }} data-testid="tracker-credentials">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: "#2F6B2A" }}><KeyRound className="w-3.5 h-3.5" /> {result.credentials.length} portal account(s) {committed ? "created" : "will be created"} — share these credentials</div>
                                {committed && <button onClick={copyCreds} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border bg-white" style={{ borderColor: "#CBE3C6", color: "#2F6B2A" }}><Copy className="w-3 h-3" /> Copy all</button>}
                            </div>
                            <div className="max-h-56 overflow-auto">
                                <table className="w-full text-[11.5px]">
                                    <thead><tr className="text-left" style={{ color: "#64718a" }}><th className="py-1 pr-3">Name</th><th className="py-1 pr-3">Email</th>{committed && <th className="py-1 pr-3">Temp password</th>}<th className="py-1">Project</th></tr></thead>
                                    <tbody>
                                        {result.credentials.map((x, i) => (
                                            <tr key={i} className="border-t" style={{ borderColor: "#E6F0E4" }}>
                                                <td className="py-1 pr-3">{x.name}</td>
                                                <td className="py-1 pr-3 font-mono">{x.email}</td>
                                                {committed && <td className="py-1 pr-3 font-mono font-bold">{x.temp_password}</td>}
                                                <td className="py-1" style={{ color: "#64718a" }}>{x.project}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {committed && <p className="text-[10.5px] mt-1.5" style={{ color: "#5a7d55" }}>Users must change their password on first login. Emails are queued until Resend is live — share these manually for now.</p>}
                        </div>
                    )}

                    {result.errors.length > 0 && (
                        <div className="rounded-[10px] border p-3 mb-3" style={{ borderColor: "#F0C9B7", background: "#FBE8E5" }} data-testid="tracker-errors">
                            <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: "#B33A2E" }}><AlertTriangle className="w-3.5 h-3.5" /> {result.errors.length} error(s)</div>
                            <ul className="mt-1.5 space-y-0.5 text-[11.5px]" style={{ color: "#7a2b22" }}>{result.errors.slice(0, 25).map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul>
                        </div>
                    )}
                    {result.exceptions.length > 0 && (
                        <div className="rounded-[10px] border p-3 mb-3" style={{ borderColor: "#F0D9B5", background: "#FCF6EA" }} data-testid="tracker-exceptions">
                            <div className="text-[12px] font-bold flex items-center gap-1.5" style={{ color: "#C68A1C" }}><AlertTriangle className="w-3.5 h-3.5" /> {result.exceptions.length} note(s) — imported but missing details</div>
                            <ul className="mt-1.5 space-y-0.5 text-[11.5px]" style={{ color: "#8a6410" }}>{result.exceptions.slice(0, 25).map((e, i) => <li key={i}>{e.project}: {e.message}</li>)}</ul>
                        </div>
                    )}

                    {committed && (
                        <button onClick={() => navigate("/app/projects")} className="mt-2 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[9px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            View projects
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
